// client/src/pages/KitchenPage.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import BrandFooter from "../components/BrandFooter";
import { logoPng } from "../config/assets";
import { getNotificationApi } from "../utils/safeNotification";

const API = process.env.REACT_APP_API_URL || "";
const TOKEN_KEY = "auth_token";
const USER_KEY = "auth_user";
const KITCHEN_TOKEN_KEY = "kitchen_token";
const KITCHEN_USER_KEY = "kitchen_user";

function safeParse(json) {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function clearGlobalKitchenAuth() {
  const localUser = safeParse(localStorage.getItem(USER_KEY));
  if (localUser?.role === "kitchen") {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }
  const sessionUser = safeParse(sessionStorage.getItem(USER_KEY));
  if (sessionUser?.role === "kitchen") {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
  }
}

function canAccessKitchenUser(u) {
  return u && ["kitchen", "owner", "superadmin"].includes(u.role);
}

/** Пара auth_token + user в session (логин с формы кухни перекрывает localStorage владельца). */
function getSessionAuthPair() {
  if (typeof sessionStorage === "undefined") return null;
  const t = sessionStorage.getItem(TOKEN_KEY);
  if (!t) return null;
  const u = safeParse(sessionStorage.getItem(USER_KEY));
  return t && u ? { token: t, user: u } : null;
}

function getInitialToken() {
  const pair = getSessionAuthPair();
  if (pair && canAccessKitchenUser(pair.user)) return pair.token;

  if (typeof localStorage !== "undefined") {
    const t = localStorage.getItem(TOKEN_KEY);
    const u = safeParse(localStorage.getItem(USER_KEY));
    if (t && canAccessKitchenUser(u)) return t;
  }
  if (typeof sessionStorage === "undefined") return null;
  return (
    sessionStorage.getItem(KITCHEN_TOKEN_KEY) || localStorage.getItem(KITCHEN_TOKEN_KEY) || null
  );
}

function getInitialUser() {
  const pair = getSessionAuthPair();
  if (pair && canAccessKitchenUser(pair.user)) return pair.user;

  if (typeof localStorage !== "undefined") {
    const u = safeParse(localStorage.getItem(USER_KEY));
    if (canAccessKitchenUser(u)) return u;
  }
  if (typeof sessionStorage === "undefined") return null;
  return (
    safeParse(sessionStorage.getItem(USER_KEY)) ||
    safeParse(sessionStorage.getItem(KITCHEN_USER_KEY)) ||
    safeParse(localStorage.getItem(KITCHEN_USER_KEY))
  );
}

// ─── Звук при новом заказе ────────────────────────────────────
function playBeep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [0, 0.2, 0.4].forEach((t) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 740;
      osc.type = "sine";
      gain.gain.setValueAtTime(0.5, ctx.currentTime + t);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + 0.18);
      osc.start(ctx.currentTime + t);
      osc.stop(ctx.currentTime + t + 0.18);
    });
  } catch (e) {}
}

// ─── Таймер ───────────────────────────────────────────────────
function useTimer(createdAt) {
  const [secs, setSecs] = useState(
    Math.floor((Date.now() - new Date(createdAt)) / 1000),
  );
  useEffect(() => {
    const id = setInterval(
      () => setSecs(Math.floor((Date.now() - new Date(createdAt)) / 1000)),
      1000,
    );
    return () => clearInterval(id);
  }, [createdAt]);
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  const level = m < 5 ? "green" : m < 20 ? "yellow" : "red";
  const COLORS = {
    green: { text: "#2e7d32", bg: "#e8f5e9", border: "#4CAF50" },
    yellow: { text: "#e65100", bg: "#FFF8E1", border: "#FF9800" },
    red: { text: "#c62828", bg: "#ffebee", border: "#f44336" },
  };
  return {
    label: `${m}:${s.toString().padStart(2, "0")}`,
    hot: m >= 20,
    level,
    colors: COLORS[level],
    secs,
  };
}

// ─── Карточка заказа ──────────────────────────────────────────
function OrderCard({ order, onStatus, loading }) {
  const sourceInfo = {
    table: { label: null, color: null, icon: null },
    delivery: { label: "ДОСТАВКА", color: "#1565C0", icon: "🛵" },
    pickup: { label: "САМОВЫВОЗ", color: "#E65100", icon: "🥡" },
  }[order.source || "table"] || { label: null, color: null, icon: null };

  const id = order.table_number
    ? `Стол ${order.table_number}`
    : order.source === "delivery" || order.source === "pickup"
      ? order.client_name || `#${String(order.id).slice(-4).toUpperCase()}`
      : `#${String(order.id).slice(-4).toUpperCase()}`;

  const { label: timerLabel, hot, colors, secs } = useTimer(order.created_at);
  const isReady = order.status === "ready";
  // Для "Готово" — фиксированное время приготовления (updated_at = момент готовности)
  const displayLabel =
    isReady && order.updated_at
      ? (() => {
          const secs = Math.floor(
            (new Date(order.updated_at) - new Date(order.created_at)) / 1000,
          );
          const m = Math.floor(secs / 60);
          const s = secs % 60;
          return `${m}:${s.toString().padStart(2, "0")}`;
        })()
      : timerLabel;
  const activeColors = isReady
    ? { text: "#2e7d32", bg: "#e8f5e9", border: "#4CAF50" }
    : order.source === "delivery"
      ? { text: "#1565C0", bg: "#E3F2FD", border: "#1565C0" }
      : order.source === "pickup"
        ? { text: "#E65100", bg: "#FFF3E0", border: "#FF9800" }
        : colors;
  const blink = !isReady && hot && Math.floor(secs) % 2 === 0;

  const NEXT = { pending: "confirmed", confirmed: "ready" };
  const BTN = { pending: "👍 Взять в работу", confirmed: "✅ Готово" };
  const next = NEXT[order.status];

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: "16px",
        padding: "14px",
        marginBottom: "12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
        borderLeft: `5px solid ${activeColors.border}`,
        opacity: loading ? 0.6 : 1,
        outline: blink
          ? `3px solid ${activeColors.border}`
          : "3px solid transparent",
        transition: "opacity 0.2s",
      }}
    >
      {/* Заголовок */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: sourceInfo.label ? "6px" : "10px",
        }}
      >
        <div style={{ fontSize: "22px", fontWeight: "800", color: "#111" }}>
          {id}
        </div>
        <div
          style={{
            fontSize: "15px",
            fontWeight: "700",
            color: activeColors.text,
            background: activeColors.bg,
            borderRadius: "8px",
            padding: "2px 10px",
          }}
        >
          ⏱ {displayLabel}
        </div>
      </div>

      {/* Бейдж доставки / самовывоза */}
      {sourceInfo.label && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            marginBottom: "8px",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              background: sourceInfo.color,
              color: "#fff",
              borderRadius: "6px",
              padding: "2px 8px",
              fontSize: "11px",
              fontWeight: "800",
              letterSpacing: "0.5px",
            }}
          >
            {sourceInfo.icon} {sourceInfo.label}
          </span>
          {order.client_phone && (
            <span
              style={{ fontSize: "12px", color: "#555", fontWeight: "600" }}
            >
              📞 {order.client_phone}
            </span>
          )}
          {order.address && (
            <span
              style={{
                fontSize: "11px",
                color: "#777",
                background: "#f5f5f5",
                borderRadius: "4px",
                padding: "2px 6px",
                maxWidth: "100%",
              }}
            >
              📍 {order.address}
            </span>
          )}
        </div>
      )}

      {/* Блюда */}
      <div style={{ marginBottom: "10px" }}>
        {(order.items || []).map((item, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: "15px",
              padding: "3px 0",
              borderBottom: "1px solid #f0f0f0",
            }}
          >
            <span style={{ fontWeight: "600" }}>{item.name}</span>
            <span
              style={{
                background: "#111",
                color: "#fff",
                borderRadius: "8px",
                padding: "1px 10px",
                fontWeight: "700",
                fontSize: "14px",
              }}
            >
              ×{item.quantity}
            </span>
          </div>
        ))}
      </div>

      {/* Заметки */}
      {(order.comment || order.notes) && (
        <div
          style={{
            fontSize: "13px",
            color: "#666",
            background: "#FFF8E1",
            borderRadius: "8px",
            padding: "6px 10px",
            marginBottom: "10px",
          }}
        >
          📝 {order.comment || order.notes}
        </div>
      )}

      {/* Кнопка */}
      {next && (
        <button
          disabled={loading}
          onClick={() => onStatus(order.id, next, order.source)}
          style={{
            width: "100%",
            padding: "10px",
            background: next === "ready" ? "#4CAF50" : "#111",
            color: "#fff",
            border: "none",
            borderRadius: "10px",
            fontWeight: "700",
            fontSize: "15px",
            cursor: loading ? "not-allowed" : "pointer",
          }}
        >
          {BTN[order.status]}
        </button>
      )}
    </div>
  );
}

// ─── Колонка ──────────────────────────────────────────────────
function Column({ title, color, bg, orders, onStatus, loadingId }) {
  return (
    <div
      style={{
        flex: "0 0 320px",
        background: bg,
        borderRadius: "16px",
        padding: "14px",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          fontWeight: "800",
          fontSize: "16px",
          color,
          marginBottom: "12px",
          textAlign: "center",
          background: color + "20",
          borderRadius: "10px",
          padding: "6px",
        }}
      >
        {title}
        <span
          style={{
            marginLeft: "8px",
            background: color,
            color: "#fff",
            borderRadius: "50%",
            width: "22px",
            height: "22px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "12px",
            fontWeight: "800",
          }}
        >
          {orders.length}
        </span>
      </div>
      <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
        {orders.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              color: "#bbb",
              padding: "30px 0",
              fontSize: "14px",
            }}
          >
            Нет заказов
          </div>
        ) : (
          orders.map((o) => (
            <OrderCard
              key={o.id}
              order={o}
              onStatus={onStatus}
              loading={loadingId === o.id}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─── Компактная строка для "Готово" ──────────────────────────
function ReadyRow({ order }) {
  const id = order.table_number
    ? `Стол ${order.table_number}`
    : order.source === "delivery" || order.source === "pickup"
      ? order.client_name || `#${String(order.id).slice(-4).toUpperCase()}`
      : `#${String(order.id).slice(-4).toUpperCase()}`;
  const srcIcon =
    order.source === "delivery"
      ? "🛵"
      : order.source === "pickup"
        ? "🥡"
        : null;

  // Фиксированное время приготовления
  const cookSecs = order.updated_at
    ? Math.floor(
        (new Date(order.updated_at) - new Date(order.created_at)) / 1000,
      )
    : null;
  const cookLabel =
    cookSecs !== null
      ? `${Math.floor(cookSecs / 60)}:${String(cookSecs % 60).padStart(2, "0")}`
      : "—";

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "7px 10px",
        borderRadius: "8px",
        background: "#fff",
        marginBottom: "5px",
        borderLeft: "3px solid #4CAF50",
        fontSize: "13px",
      }}
    >
      <span style={{ fontWeight: "700", color: "#111" }}>
        {srcIcon && `${srcIcon} `}
        {id}
      </span>
      <span style={{ color: "#666", fontSize: "12px" }}>
        {order.items?.length || 0} блюд
      </span>
      <span
        style={{
          color: "#2e7d32",
          fontWeight: "700",
          fontSize: "12px",
          background: "#e8f5e9",
          borderRadius: "6px",
          padding: "2px 7px",
        }}
      >
        ✅ {cookLabel}
      </span>
    </div>
  );
}

// ─── Колонка "Готово" — компактная с фильтром ─────────────────
function ReadyColumn({ orders }) {
  const [hoursFilter, setHoursFilter] = useState(2);
  const [collapsed, setCollapsed] = useState(false);

  const now = Date.now();
  const filtered = orders.filter((o) => {
    const t = new Date(o.updated_at || o.created_at).getTime();
    return now - t <= hoursFilter * 3600 * 1000;
  });
  const hidden = orders.length - filtered.length;

  return (
    <div
      style={{
        width: collapsed ? "48px" : "260px",
        minWidth: collapsed ? "48px" : "260px",
        minHeight: 0,
        alignSelf: "stretch",
        background: "#F1FFF4",
        borderRadius: "16px",
        padding: collapsed ? "14px 8px" : "14px",
        display: "flex",
        flexDirection: "column",
        transition: "width 0.3s, min-width 0.3s",
        overflow: "hidden",
      }}
    >
      {/* Заголовок */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: collapsed ? 0 : "10px",
        }}
      >
        {!collapsed && (
          <div
            style={{
              fontWeight: "800",
              fontSize: "15px",
              color: "#4CAF50",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            🟢 ГОТОВО
            <span
              style={{
                background: "#4CAF50",
                color: "#fff",
                borderRadius: "50%",
                width: "22px",
                height: "22px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                fontWeight: "800",
              }}
            >
              {orders.length}
            </span>
          </div>
        )}
        <button
          onClick={() => setCollapsed((c) => !c)}
          style={{
            background: "#4CAF5020",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            fontSize: "16px",
            padding: "4px 8px",
            color: "#4CAF50",
            fontWeight: "800",
            marginLeft: collapsed ? 0 : "auto",
          }}
          title={collapsed ? "Развернуть" : "Свернуть"}
        >
          {collapsed ? `✅${orders.length}` : "◀"}
        </button>
      </div>

      {!collapsed && (
        <>
          {/* Фильтр по времени */}
          <div
            style={{
              display: "flex",
              gap: "4px",
              marginBottom: "10px",
              flexWrap: "wrap",
            }}
          >
            {[1, 2, 4, 8].map((h) => (
              <button
                key={h}
                onClick={() => setHoursFilter(h)}
                style={{
                  padding: "3px 8px",
                  borderRadius: "8px",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "11px",
                  fontWeight: "700",
                  background: hoursFilter === h ? "#4CAF50" : "#e8f5e9",
                  color: hoursFilter === h ? "#fff" : "#2e7d32",
                }}
              >
                {h}ч
              </button>
            ))}
          </div>

          {/* Список */}
          <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
            {filtered.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  color: "#aaa",
                  padding: "20px 0",
                  fontSize: "13px",
                }}
              >
                Нет заказов
              </div>
            ) : (
              filtered.map((o) => <ReadyRow key={o.id} order={o} />)
            )}
            {hidden > 0 && (
              <div
                style={{
                  textAlign: "center",
                  color: "#aaa",
                  fontSize: "12px",
                  padding: "8px",
                  background: "#e8f5e9",
                  borderRadius: "8px",
                }}
              >
                + ещё {hidden} старых заказов
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Логин ────────────────────────────────────────────────────
function KitchenLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || "Ошибка входа");
      if (!["kitchen", "owner", "superadmin"].includes(data.role)) {
        throw new Error("Нет доступа к кухне");
      }
      onLogin(data.token, data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#111",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          background: "#fff",
          borderRadius: "20px",
          padding: "40px",
          width: "320px",
        }}
      >
        <div
          style={{ textAlign: "center", fontSize: "40px", marginBottom: "8px" }}
        >
          🍳
        </div>
        <h2 style={{ textAlign: "center", marginBottom: "24px" }}>Кухня</h2>

        {error && (
          <div
            style={{
              background: "#ffebee",
              color: "#c62828",
              borderRadius: "8px",
              padding: "10px",
              marginBottom: "16px",
              fontSize: "14px",
            }}
          >
            {error}
          </div>
        )}

        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          style={inputStyle}
        />
        <input
          type="password"
          placeholder="Пароль"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          style={inputStyle}
        />
        <button type="submit" disabled={loading} style={btnStyle}>
          {loading ? "Вход..." : "Войти"}
        </button>
      </form>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: "12px",
  marginBottom: "12px",
  border: "1px solid #ddd",
  borderRadius: "10px",
  fontSize: "15px",
  boxSizing: "border-box",
};
const btnStyle = {
  width: "100%",
  padding: "13px",
  background: "#111",
  color: "#fff",
  border: "none",
  borderRadius: "10px",
  fontWeight: "700",
  fontSize: "16px",
  cursor: "pointer",
};

// ─── Главная страница кухни ───────────────────────────────────
export default function KitchenPage() {
  useEffect(() => {
    document.title = "Кухня — KDS";
  }, []);
  const [token, setToken] = useState(getInitialToken);
  const [user, setUser] = useState(getInitialUser);
  const [orders, setOrders] = useState([]);
  const [loadingId, setLoadingId] = useState(null);
  const prevIds = useRef(new Set());

  // Миграция legacy kitchen_token из localStorage в sessionStorage
  useEffect(() => {
    const legacyToken = localStorage.getItem(KITCHEN_TOKEN_KEY);
    const legacyUser = localStorage.getItem(KITCHEN_USER_KEY);
    if (legacyToken && !sessionStorage.getItem(TOKEN_KEY)) {
      sessionStorage.setItem(TOKEN_KEY, legacyToken);
    }
    if (legacyToken && !sessionStorage.getItem(KITCHEN_TOKEN_KEY)) {
      sessionStorage.setItem(KITCHEN_TOKEN_KEY, legacyToken);
    }
    if (legacyUser && !sessionStorage.getItem(USER_KEY)) {
      sessionStorage.setItem(USER_KEY, legacyUser);
    }
    if (legacyUser && !sessionStorage.getItem(KITCHEN_USER_KEY)) {
      sessionStorage.setItem(KITCHEN_USER_KEY, legacyUser);
    }
    if (legacyToken || legacyUser) {
      localStorage.removeItem(KITCHEN_TOKEN_KEY);
      localStorage.removeItem(KITCHEN_USER_KEY);
    }
  }, []);

  const handleLogin = useCallback((tok, userData) => {
    const serializedUser = JSON.stringify(userData);
    sessionStorage.setItem(TOKEN_KEY, tok);
    sessionStorage.setItem(USER_KEY, serializedUser);
    sessionStorage.setItem(KITCHEN_TOKEN_KEY, tok);
    sessionStorage.setItem(KITCHEN_USER_KEY, serializedUser);
    setToken(tok);
    setUser(userData);
  }, []);

  const handleLogout = useCallback(() => {
    clearGlobalKitchenAuth();
    sessionStorage.removeItem(KITCHEN_TOKEN_KEY);
    sessionStorage.removeItem(KITCHEN_USER_KEY);
    // legacy cleanup
    localStorage.removeItem(KITCHEN_TOKEN_KEY);
    localStorage.removeItem(KITCHEN_USER_KEY);
    setToken(null);
    setUser(null);
    setOrders([]);
  }, []);

  const fetchOrders = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API}/api/kitchen/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.status === 401) {
        handleLogout();
        return;
      }
      const data = await r.json();
      if (!Array.isArray(data)) {
        console.warn("Kitchen API не вернул массив:", r.status, data);
        return;
      }

      // Звук при новых pending
      const newPending = data.filter(
        (o) => o.status === "pending" && !prevIds.current.has(o.id),
      );
      if (newPending.length > 0) {
        playBeep();
        const N = getNotificationApi();
        if (N && N.permission === "granted") {
          // eslint-disable-next-line new-cap
          new N("🍳 Новый заказ!", {
            body: newPending
              .map((o) => {
                if (o.table_number) return `Стол ${o.table_number}`;
                if (o.source === "delivery")
                  return `🛵 ${o.client_name || o.address || o.id}`;
                if (o.source === "pickup") return `🥡 ${o.client_name || o.id}`;
                return `#${String(o.id).slice(-4).toUpperCase()}`;
              })
              .join(", "),
            icon: logoPng(),
          });
        }
      }
      prevIds.current = new Set(data.map((o) => o.id));
      setOrders(data);
    } catch (err) {
      console.error("Kitchen fetch error:", err);
    }
  }, [token, handleLogout]);

  // Polling каждые 8 сек
  useEffect(() => {
    if (!token) return;
    fetchOrders();
    const id = setInterval(fetchOrders, 8000);
    return () => clearInterval(id);
  }, [token, fetchOrders]);

  // Запрос разрешения на уведомления
  useEffect(() => {
    const N = getNotificationApi();
    if (N && N.permission === "default") {
      N.requestPermission();
    }
  }, []);

  const handleStatus = useCallback(
    async (orderId, status, source) => {
      setLoadingId(orderId);
      try {
        await fetch(`${API}/api/kitchen/orders/${orderId}/status`, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status, source }),
        });
        await fetchOrders();
      } finally {
        setLoadingId(null);
      }
    },
    [token, fetchOrders],
  );

  if (!token) return <KitchenLogin onLogin={handleLogin} />;

  // Сортировка: самый старый вверху (по времени ожидания)
  const sortByCreated = (a, b) =>
    new Date(a.created_at) - new Date(b.created_at);
  const sortByUpdated = (a, b) =>
    new Date(a.updated_at || a.created_at) -
    new Date(b.updated_at || b.created_at);
  const newOrders = orders
    .filter((o) => o.status === "pending")
    .sort(sortByCreated);
  const cookingOrders = orders
    .filter((o) => o.status === "confirmed")
    .sort(sortByCreated);
  const readyOrders = orders
    .filter((o) => o.status === "ready")
    .sort(sortByUpdated);

  return (
    <div
      style={{
        height: "100vh",
        background: "#1a1a1a",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Предупреждение на телефоне */}
      <style>{`
        @media (max-width: 600px) { .kds-mobile-warn { display: flex !important; } }
      `}</style>
      <div
        className="kds-mobile-warn"
        style={{
          display: "none",
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          background: "#111",
          color: "#fff",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: "30px",
        }}
      >
        <div style={{ fontSize: "48px", marginBottom: "16px" }}>📱➡️📟</div>
        <div
          style={{ fontSize: "20px", fontWeight: "800", marginBottom: "8px" }}
        >
          KDS только для планшета
        </div>
        <div style={{ fontSize: "14px", color: "#aaa" }}>
          Откройте страницу на планшете
          <br />в горизонтальной ориентации
        </div>
      </div>
      {/* Хедер */}
      <div
        style={{
          background: "#111",
          color: "#fff",
          padding: "12px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span style={{ fontSize: "24px" }}>🍳</span>
          <div>
            <div style={{ fontWeight: "800", fontSize: "18px" }}>КУХНЯ</div>
            <div style={{ fontSize: "12px", color: "#aaa" }}>{user?.email}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {newOrders.length > 0 && (
            <span
              style={{
                background: "#f44336",
                color: "#fff",
                borderRadius: "20px",
                padding: "4px 12px",
                fontWeight: "700",
                fontSize: "13px",
              }}
            >
              🔴 {newOrders.length} новых
            </span>
          )}
          {cookingOrders.length > 0 && (
            <span
              style={{
                background: "#FF9800",
                color: "#fff",
                borderRadius: "20px",
                padding: "4px 12px",
                fontWeight: "700",
                fontSize: "13px",
              }}
            >
              🟡 {cookingOrders.length} готовится
            </span>
          )}
          <button
            onClick={handleLogout}
            style={{
              background: "#333",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              padding: "6px 14px",
              cursor: "pointer",
              fontSize: "13px",
            }}
          >
            Выйти
          </button>
        </div>
      </div>

      {/* Колонки — высота = экран, каждая скроллится внутри */}
      <div
        style={{
          display: "flex",
          gap: "12px",
          padding: "16px",
          flex: 1,
          minHeight: 0,
          overflowX: "auto",
          overflowY: "hidden",
        }}
      >
        <Column
          title="🔴 НОВЫЕ"
          color="#f44336"
          bg="#fff5f5"
          orders={newOrders}
          onStatus={handleStatus}
          loadingId={loadingId}
        />
        <Column
          title="🟡 ГОТОВИТСЯ"
          color="#FF9800"
          bg="#FFF8F0"
          orders={cookingOrders}
          onStatus={handleStatus}
          loadingId={loadingId}
        />
        <ReadyColumn orders={readyOrders} />
      </div>

      <BrandFooter dark />
    </div>
  );
}
